import { useCallback, useState } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DeleteIcon, ModeEditIcon } from "@l2r-front/l2r-icons"
import { useAlertsDispatchContext } from "@l2r-front/l2r-query"
import { I18N_NAMESPACE, STATUS_DELIVERED, STATUS_IN_PROGRESS, useCampaign } from "@l2r-front/l2r-shared-campaign"
import { MoreMenu } from "@l2r-front/l2r-ui"
import { NavigationOrigins, useNavigateWithSearchParams } from "@l2r-front/l2r-utils"

import { useCampaignStateContext } from "../../contexts/CampaignContext"
import { useCampaignDelete } from "../../hooks/mutations/useCampaignDelete"
import { useCampaignTexts } from "../../hooks/useCampaignTexts"
import { useIsCampaignWriter } from "../../hooks/useIsCampaignWriter"

import { CampaignDetailSidebarContent } from "../CampaignDetailSidebarContent"
import { CampaignEditModal } from "../CampaignEditModal"

import * as Styled from "./CampaignDetailSidebar.styled"

export function CampaignDetailSidebar() {

    const { t } = useTranslation([I18N_NAMESPACE])
    const navigate = useNavigateWithSearchParams()
    const { campaignId } = useParams()
    const { data: campaign, isLoading: isCampaignLoading } = useCampaign(campaignId)
    const isCampaignWriter = useIsCampaignWriter(campaign)
    const [editModalOpen, setEditModalOpen] = useState(false)
    const { setAlert } = useAlertsDispatchContext()
    const campaignTexts = useCampaignTexts(campaign?.campaign_type, t)
    const { isExportRunning } = useCampaignStateContext()

    const navigateBack = useCallback(() => {
        navigate("..", { relative: "path", navigationOrigin: NavigationOrigins.ROAD_FROM_BACK_DETAIL })
    }, [navigate])

    const deleteMutation = useCampaignDelete(campaign?.uuid, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "components.campaignMenu.delete.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "components.campaignMenu.delete.snackbar.success"),
        onSuccess: () => {
            navigateBack()
        },
    })

    const handleDeleteCampaign = () => {
        setAlert({
            alertTitle: t(I18N_NAMESPACE, "components.campaignMenu.delete.confirm.label"),
            alertText: campaignTexts?.["deleteCampaignDesc"],
            cancelText: t(I18N_NAMESPACE, "components.campaignMenu.delete.confirm.cancel"),
            confirmText: t(I18N_NAMESPACE, "components.campaignMenu.delete.confirm.validate"),
            callback: () => {
                deleteMutation.mutate({})
            },
        })
    }

    if (isCampaignLoading || !campaign) {
        return null
    }

    return (
        <Styled.BottomSheetContent
            id="campaign-detail-sidebar"
            onBack={navigateBack}
            title={t(I18N_NAMESPACE, "containers.campaignDetailSidebar.title")}
            titleSideComponent={<MoreMenu
                disabled={!!isExportRunning?.[campaign?.uuid]}
                menuConfig={[
                    {
                        displayed: !!isCampaignWriter && campaign.status !== STATUS_DELIVERED,
                        id: "edit-campaign",
                        label: t(I18N_NAMESPACE, "components.campaignMenu.edit.label"),
                        icon: <ModeEditIcon color="common/black_states" />,
                        props: {
                            onClick: () => setEditModalOpen(true),
                        },
                    },
                    {
                        displayed: campaign.status === STATUS_IN_PROGRESS && isCampaignWriter,
                        id: "delete-campaign",
                        label: t(I18N_NAMESPACE, "components.campaignMenu.delete.label"),
                        icon: <DeleteIcon color="common/black_states" />,
                        props: {
                            onClick: handleDeleteCampaign,
                        },
                    },
                ]}
            />}
        >
            <CampaignDetailSidebarContent campaign={campaign} />
            <CampaignEditModal
                campaign={campaign}
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
            />
        </Styled.BottomSheetContent>
    )
}
